import { Component } from "react";
import { SHUFFLE_START_DELAY_MS } from "../constants";
import { AUDIO_MODE_PROPS } from "./audio-modes";
import { popupComponentDefaultProps } from "./default-props";
import ActionsFooter from "./actions-footer";
import DebugBar from "./debug-bar";
import EffectsSection from "./effects-section";
import PopupHeader from "./popup-header";
import SlowedAI from "./slowed-ai";
import SlowControlSection from "./slow-control-section";
import {
  hasShuffleSyncPropsChanged,
  seedSliderTouchedKeys,
  sliderMidValue,
  sliderValueFromClientX,
} from "./tile-slider";

class PopupComponent extends Component {
  constructor(props) {
    super(props);

    this.sliderTouched = {};
    this.tileDrag = null;
    this.suppressAdjustableClick = false;
    this.state = {
      mount: false,
      debugModeEnabled: false,
      draggingTileKey: null,
      holdingTileKey: null,
      releasingTileKey: null,
      fillGrowingKeys: {},
      shuffleBusy: false,
      subscriptionStatusStep: 0,
    };
  }

  componentDidMount() {
    setTimeout(() => this.setState({ mount: true }), 200);
    this.scheduleSubscriptionStatusRotation();
    this.seedSliderTouched(this.props);
  }

  componentDidUpdate(prevProps) {
    const trialChanged =
      prevProps.trialMode !== this.props.trialMode ||
      prevProps.remainingTrialDays !== this.props.remainingTrialDays ||
      prevProps.isPro !== this.props.isPro;
    if (trialChanged) this.scheduleSubscriptionStatusRotation();

    AUDIO_MODE_PROPS.forEach(({ key, prop, isActive }) => {
      const wasActive = isActive ? isActive(prevProps) : prevProps[prop];
      const isActiveNow = isActive ? isActive(this.props) : this.props[prop];
      if (!wasActive && isActiveNow) {
        if (!this.state.fillGrowingKeys[key]) {
          this.startFillGrow(key);
        }
      } else if (wasActive && !isActiveNow) {
        this.clearFillGrow(key);
      }
    });

    if (hasShuffleSyncPropsChanged(prevProps, this.props)) {
      this.seedSliderTouched(this.props);
    }
  }

  startFillGrow(key) {
    this.setState(
      (state) => ({
        fillGrowingKeys: { ...state.fillGrowingKeys, [key]: true },
      }),
      () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.clearFillGrow(key);
          });
        });
      },
    );
  }

  clearFillGrow(key) {
    if (!this.state.fillGrowingKeys[key]) return;
    this.setState((state) => {
      const fillGrowingKeys = { ...state.fillGrowingKeys };
      delete fillGrowingKeys[key];
      return { fillGrowingKeys };
    });
  }

  componentWillUnmount() {
    clearTimeout(this.subscriptionStatusTimer);
    clearTimeout(this.shuffleStartTimer);
    clearTimeout(this.releaseTileTimer);
    this.tileDrag = null;
  }

  seedSliderTouched(props) {
    Object.assign(this.sliderTouched, seedSliderTouchedKeys(props));
  }

  finishTileInteraction(modeKey, { animateRelease = false } = {}) {
    this.tileDrag = null;
    clearTimeout(this.releaseTileTimer);

    const shouldAnimate =
      animateRelease &&
      modeKey &&
      (this.state.holdingTileKey === modeKey ||
        this.state.draggingTileKey === modeKey);

    if (shouldAnimate) {
      this.setState({
        draggingTileKey: null,
        holdingTileKey: null,
        releasingTileKey: modeKey,
      });
      this.releaseTileTimer = setTimeout(() => {
        this.setState({ releasingTileKey: null });
      }, 380);
      return;
    }

    if (
      this.state.draggingTileKey ||
      this.state.holdingTileKey ||
      this.state.releasingTileKey
    ) {
      this.setState({
        draggingTileKey: null,
        holdingTileKey: null,
        releasingTileKey: null,
      });
    }
  }

  updateTileSliderFromPointer(clientX, mode, el) {
    if (!mode.slider || !el) return;

    const value = sliderValueFromClientX(clientX, el, mode.slider);
    mode.slider.onChange(value);
    this.sliderTouched[mode.key] = true;
  }

  applyDefaultSliderIfNeeded(mode) {
    if (!mode.slider || this.sliderTouched[mode.key]) return;

    const defaultVal =
      mode.slider.defaultValue !== undefined
        ? mode.slider.defaultValue
        : sliderMidValue(mode.slider);
    mode.slider.onChange(defaultVal);
    this.sliderTouched[mode.key] = true;
  }

  onAdjustableTilePointerDown(event, mode) {
    if (this.props.isProFeaturesLocked) return;

    const wasActive = !!mode.active;

    this.tileDrag = {
      key: mode.key,
      pointerId: event.pointerId,
      startX: event.clientX,
      dragging: false,
      el: event.currentTarget,
      slider: mode.slider,
      wasActive,
      isActive: wasActive,
      activatedOnDown: false,
    };

    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch (_error) {
      // Ignore capture failures in unsupported environments.
    }

    if (!wasActive) {
      this.applyDefaultSliderIfNeeded(mode);
      mode.onToggle();
      this.tileDrag.isActive = true;
      this.tileDrag.activatedOnDown = true;
      this.setState(
        {
          holdingTileKey: mode.key,
          fillGrowingKeys: {
            ...this.state.fillGrowingKeys,
            [mode.key]: true,
          },
        },
        () => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.clearFillGrow(mode.key);
            });
          });
        },
      );
      return;
    }

    this.setState({ holdingTileKey: mode.key });
  }

  onAdjustableTilePointerMove(event) {
    if (!this.tileDrag || event.pointerId !== this.tileDrag.pointerId) return;
    if (!this.tileDrag.isActive) return;

    if (Math.abs(event.clientX - this.tileDrag.startX) <= 4) return;

    if (!this.tileDrag.dragging) {
      this.tileDrag.dragging = true;
      this.clearFillGrow(this.tileDrag.key);
      if (this.state.draggingTileKey !== this.tileDrag.key) {
        this.setState({ draggingTileKey: this.tileDrag.key });
      }
    }

    this.updateTileSliderFromPointer(
      event.clientX,
      { key: this.tileDrag.key, slider: this.tileDrag.slider },
      this.tileDrag.el,
    );
  }

  onAdjustableTilePointerUp(event, mode) {
    if (!this.tileDrag || event.pointerId !== this.tileDrag.pointerId) return;

    const { dragging, wasActive } = this.tileDrag;
    const shouldAnimateRelease =
      dragging && this.state.holdingTileKey === mode.key;

    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch (_error) {
      // Ignore release failures in unsupported environments.
    }

    this.finishTileInteraction(mode.key, {
      animateRelease: shouldAnimateRelease,
    });

    this.suppressAdjustableClick = true;

    if (dragging) return;

    if (wasActive) {
      mode.onToggle();
    }
  }

  onAdjustableTileClick(mode) {
    if (this.props.isProFeaturesLocked) {
      this.props.onUpgradeClick();
      return;
    }

    if (this.suppressAdjustableClick) {
      this.suppressAdjustableClick = false;
      return;
    }

    this.handleAdjustableTileTap(mode);
  }

  handleAdjustableTileTap(mode) {
    if (mode.active) {
      mode.onToggle();
      return;
    }

    this.applyDefaultSliderIfNeeded(mode);
    mode.onToggle();
  }

  onAdjustableTilePointerCancel(event) {
    if (!this.tileDrag || event.pointerId !== this.tileDrag.pointerId) return;

    const { key, dragging } = this.tileDrag;

    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch (_error) {
      // Ignore capture failures in unsupported environments.
    }

    this.finishTileInteraction(key, {
      animateRelease: dragging && this.state.holdingTileKey === key,
    });
  }

  async runShuffle() {
    if (this.state.shuffleBusy) return;
    if (this.props.isProFeaturesLocked) {
      this.props.onUpgradeClick();
      return;
    }

    this.setState({ shuffleBusy: true });
    try {
      await new Promise((resolve) => {
        this.shuffleStartTimer = setTimeout(resolve, SHUFFLE_START_DELAY_MS);
      });
      return await this.props.onAIShuffle();
    } finally {
      clearTimeout(this.shuffleStartTimer);
      this.setState({ shuffleBusy: false });
    }
  }

  async runMood(mood) {
    if (this.state.shuffleBusy) return;
    if (this.props.isProFeaturesLocked) {
      this.props.onUpgradeClick();
      return;
    }

    this.setState({ shuffleBusy: true });
    try {
      return await this.props.onApplyMood?.(mood);
    } finally {
      this.setState({ shuffleBusy: false });
    }
  }

  scheduleSubscriptionStatusRotation() {
    clearTimeout(this.subscriptionStatusTimer);
    const { trialMode, remainingTrialDays, isPro } = this.props;
    const shouldRotate = !isPro && trialMode && remainingTrialDays >= 0;
    if (!shouldRotate) {
      this.setState({ subscriptionStatusStep: 0 });
      return;
    }
    const dwell = [4500, 2400, 2400];
    let step = 0;
    const tick = () => {
      step = (step + 1) % 3;
      this.setState({ subscriptionStatusStep: step });
      this.subscriptionStatusTimer = setTimeout(tick, dwell[step]);
    };
    this.subscriptionStatusTimer = setTimeout(tick, 2000);
  }

  render() {
    const {
      slowControl,
      isSlowedEnabled,
      error,
      notSupported,
      musicProOnly,
      isHeaderVisible,
      isPro,
      trialMode,
      darkMode,
      remainingTrialDays,
      isFeedbackEnabled,
      isRateAppEnabled,
      isProFeaturesLocked,
      contentApplying,
      billingLoading,
    } = this.props;

    const { debugModeEnabled } = this.state;
    const showSlowedAI =
      this.props.isSlowedAIEnabled !== false &&
      !notSupported &&
      !musicProOnly &&
      isSlowedEnabled;

    if (error) {
      return (
        <div className="popup">
          <div className="content">
            <p>
              {process.env.DEBUG === "true"
                ? error.message
                : "Something went wrong"}
            </p>
          </div>
        </div>
      );
    }

    const tileHandlers = {
      onAdjustablePointerDown: (event, mode) =>
        this.onAdjustableTilePointerDown(event, mode),
      onAdjustablePointerMove: (event) =>
        this.onAdjustableTilePointerMove(event),
      onAdjustablePointerUp: (event, mode) =>
        this.onAdjustableTilePointerUp(event, mode),
      onAdjustablePointerCancel: (event) =>
        this.onAdjustableTilePointerCancel(event),
      onAdjustableClick: (mode) => this.onAdjustableTileClick(mode),
    };

    const videoFx = this.props.videoFx;
    const slowedFxActive = isSlowedEnabled && videoFx === "slowed";

    return (
      <div id="Slowed" className={darkMode ? "dark-mode" : null}>
        {this.props.testMode && (
          <div className="test-mode-bar" role="status">
            Test mode
          </div>
        )}
        <div
          className={`ambient-bg${slowedFxActive ? " ambient-bg--active" : ""}`}
        >
          <div className="ambient-bg__field">
            <div className="ambient-bg__glow ambient-bg__glow--1" />
            <div className="ambient-bg__glow ambient-bg__glow--2" />
            <div className="ambient-bg__glow ambient-bg__glow--3" />
          </div>
        </div>
        <div className={`popup ${this.state.mount ? "" : "mounting"}`}>
          {isHeaderVisible && (
            <PopupHeader
              isPro={isPro}
              trialMode={trialMode}
              remainingTrialDays={remainingTrialDays}
              subscriptionStatusStep={this.state.subscriptionStatusStep}
              notSupported={notSupported}
              musicProOnly={musicProOnly}
              isSlowedEnabled={isSlowedEnabled}
              slowedFxActive={slowedFxActive}
              contentApplying={contentApplying}
              mount={this.state.mount}
              billingLoading={billingLoading}
              onUpgradeClick={() => this.props.onUpgradeClick()}
              onManageSubscriptionClick={() =>
                this.props.onManageSubscriptionClick()
              }
              onDebugToggle={() =>
                this.setState({ debugModeEnabled: !debugModeEnabled })
              }
              onSlowedToggle={(value) => this.props.onSlowedToggle(value)}
              hideManagePlan={this.props.hideManagePlan}
            />
          )}

          <div
            className={`content ${(!isSlowedEnabled || musicProOnly) && "content--disabled"}`}
          >
            {musicProOnly ? (
              <div className="content__overlay content__overlay--music-pro">
                <p className="content__overlay-title">YouTube Music</p>
                <p className="content__overlay-text">
                  Slowing tracks on{" "}
                  <strong>music.youtube.com</strong> is included with Slowed Pro.
                </p>
                <button
                  type="button"
                  className="content__overlay-cta"
                  onClick={() => this.props.onUpgradeClick()}
                >
                  Unlock Pro
                </button>
              </div>
            ) : (
              !isSlowedEnabled && <div className="content__overlay" />
            )}

            <SlowControlSection
              slowControl={slowControl}
              onSlowControlChange={(value) =>
                this.props.onSlowControlChange(value)
              }
            />

            <EffectsSection
              props={this.props}
              tileState={this.state}
              tileHandlers={tileHandlers}
            />

            <ActionsFooter
              darkMode={darkMode}
              isProFeaturesLocked={isProFeaturesLocked}
              isFeedbackEnabled={isFeedbackEnabled}
              isRateAppEnabled={isRateAppEnabled}
              onDarkModeChange={(value) => this.props.onDarkModeChange(value)}
              onCustomizeShortcut={() => this.props.onCustomizeShortcut()}
              onHelpClick={() => this.props.onHelpClick()}
              onFeedbackClick={() => this.props.onFeedbackClick()}
              onRateAppClick={() => this.props.onRateAppClick()}
              onChangelogClick={() => this.props.onChangelogClick()}
            />
          </div>

          {showSlowedAI && (
            <SlowedAI
              isProFeaturesLocked={isProFeaturesLocked}
              trackName={this.props.trackName}
              onUpgradeClick={() => this.props.onUpgradeClick()}
              onGenerateMood={() => this.runShuffle()}
              onApplyMood={(mood) => this.runMood(mood)}
              onOpen={(metadata) => this.props.onSlowedAiOpen(metadata)}
              onLockedClick={(metadata) =>
                this.props.onSlowedAiLockedClick(metadata)
              }
            />
          )}
        </div>
          {debugModeEnabled && (
            <DebugBar
              testMode={this.props.testMode}
              paymentHookId={this.props.paymentHookId}
              hookBusy={this.props.hookBusy}
              hookError={this.props.hookError}
              trialMode={trialMode}
              onTestModeChange={(value) => this.props.onTestModeChange(value)}
              onRefreshPaymentHookClick={() =>
                this.props.onRefreshPaymentHookClick()
              }
              onEndTrialModeClick={() => this.props.onEndTrialModeClick()}
              onStartTrialModeClick={() => this.props.onStartTrialModeClick()}
            />
          )}
      </div>
    );
  }
}

PopupComponent.defaultProps = popupComponentDefaultProps;

self.Slowed.Components.Popup = PopupComponent;

export default PopupComponent;
